/* eslint-disable no-magic-numbers */

import { takeWhile } from 'lodash';

import { FinancialConfig, Forecast } from '@futrli-sdk/predict';
import { Organisation } from '@futrli-sdk/organisations';
import { ROUTES } from '@futrli/shared-services';

import { format, startOfDay } from 'date-fns';
import { forceLocalDate } from '../../../../utils/dates';
import { getColumnsWithFYInfo } from '../../predictions/utils/forecast-columns';
import { isPastCell } from '../../../../ui/data-grid/components/current-period/current-period';
import { PeriodType, PERIOD_TYPE_REQUEST_DATA } from '../../../../core/constants';
import { QueryParams } from '../../../../core/types';
import { ReportData } from './types';

/**
 * Generate query params (date window for report)
 */
export const generateQueryParams = (prevPeriodType: PeriodType, newPeriodType?: PeriodType): QueryParams => {
  const periodType = newPeriodType || prevPeriodType;
  return PERIOD_TYPE_REQUEST_DATA[periodType] as QueryParams;
};

/**
 * Returns the report columns from the report data
 * @param data - The report data
 */
export const getForecastReportColumns = (reportData?: ReportData) => {
  return reportData?.forecastReportV2?.data.columns || [];
};

/**
 * returns the date the graph should be focused on based on forecast type
 * @param forecast - The forecast data
 * @param organisation - The data for the organisation the forecast belongs to
 */
export const getForecastGraphDate = (
  forecast: Forecast | null | undefined,
  organisation: Organisation | null | undefined
) => {
  let dateToCheck = startOfDay(new Date(Date.now()));

  if (forecast) {
    // For scenario and budget we need to find forecast from date
    if (forecast.kind === 'scenario' || forecast.kind === 'budget') {
      dateToCheck = startOfDay(
        forecast?.forecast_from_date ? forceLocalDate(forecast.forecast_from_date) : new Date(Date.now())
      );
    } else if (forecast.kind === 'live' && organisation) {
      // For live forecast we need to find last sync date
      dateToCheck = startOfDay(forceLocalDate(organisation?.datasource.status.modified));
    }
  }
  return format(dateToCheck, 'yyyy-MM-dd');
};

/**
 * Gets the forecastId from page URL
 */
export const getForecastIdFromURL = (url = window.location.href): string | undefined => {
  const splitCurrentPath = url.split(ROUTES.FORECASTS);
  if (splitCurrentPath.length === 2) {
    const forecastDetailsPart = splitCurrentPath[1];
    const forecastDetailsParts = forecastDetailsPart.split('/');
    if (forecastDetailsParts.length >= 2) {
      return forecastDetailsParts[1];
    }
  }
};

/**
 * Gets the last sync date for the organisation
 * @param organisation - The organisation object
 */
export const getOrganisationLastSync = (organisation: Organisation) => {
  return format(startOfDay(forceLocalDate(organisation.datasource.status.modified)), 'yyyy-MM-dd');
};

/**
 * Gets the total number of columns (dates) that are in the past when compared to forecastFromDate
 * This is used for determining initial scroll to the left of data grids.
 *
 * @param columns - The columns to check
 * @param periodType - The period type
 * @param financialConfig - The financial configuration
 * @param forecastFromDate - The forecast from date
 */
export const getTotalPastDates = (
  columns: string[],
  periodType: PeriodType,
  financialConfig?: FinancialConfig,
  forecastFromDate?: string | null
) => {
  const pastColumns = takeWhile(columns, (item: string) => isPastCell(item, periodType, forecastFromDate));
  if (financialConfig) {
    const pastColumnsWithFYInfo = getColumnsWithFYInfo(pastColumns, financialConfig);
    return pastColumnsWithFYInfo.length;
  }
  return pastColumns.length;
};
